import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Box, Typography, useTheme } from '@mui/material';
import { useToggle } from 'usehooks-ts';

import {
  ArrayAnswer,
  ProcessTalentRegistrationFormDto,
  ProcessTalentRegistrationFormDtoType,
  ProcessTalentResponseDTO,
  SingleAnswer,
  TalentRegistrationFormAnswersDto,
  useGetTalentRegistrationForm,
  useUpdateTalentRegistrationForm
} from '@/api/generated';
import AdditionalInfoQuestion from '@/components/AdditionalInfoQuestion';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import EmptyState from '@/components/EmptyState';
import { CommonFormFooter } from '@/components/Form/Form';
import Loader from '@/components/Loader/Loader';
import { HorizontalTab, HorizontalTabs } from '@/components/Tabs';
import { useTalentPermissions } from '@/hooks/permissions/useTalentPermissions';
import useConfirmation from '@/hooks/useConfirmation';
import { useSnackbar } from '@/hooks/useSnackbar';

enum Variant {
  INTERNAL = 'internal',
  EXTERNAL = 'external'
}

type RegistrationFormValues = { [id: string]: Exclude<ProcessTalentRegistrationFormDto['answer'], undefined>['value'] };

const AdditionalInfo = ({
  process,
  talentId,
  footer
}: {
  process: Pick<ProcessTalentResponseDTO, 'id'>;
  talentId: string;
  footer?: ReactNode;
}) => {
  const { t, i18n } = useTranslation(['talents', 'validation', 'settings']);
  const { spacing, typography, palette } = useTheme();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const [infoVariant, setInfoVariant] = useState(Variant.EXTERNAL);
  const [proceedToTab, setProceedToTab] = useState<() => void>();
  const [isTabChangeConfirmationOpen, toggleTabChangeConfirmation] = useToggle(false);
  const formMethods = useForm<RegistrationFormValues>();
  const { reset: resetForm, formState, getValues, getFieldState } = formMethods;
  const resetDefaultValuesBlocker = useRef(false);
  const {
    data: registrationForm,
    isLoading,
    queryKey: talentRegistrationFormQueryKey
  } = useGetTalentRegistrationForm(process.id!, talentId, {
    query: {
      suspense: false,
      onSuccess(data) {
        setDefaultFields(data);
      }
    }
  });
  const { mutateAsync: updateTalentRegistrationForm, isLoading: isFormRegistrationSaving } =
    useUpdateTalentRegistrationForm();
  const translations = registrationForm?.translations?.[i18n.language] ?? registrationForm?.translations?.EN ?? {};
  const { proceed, reset: resetBlocker, isBlocked } = useConfirmation(formState);

  const handleSubmitFailed = useCallback(() => {
    showMessage(t('validation:alerts.failed'));
    resetBlocker?.();
  }, [resetBlocker, showMessage, t]);

  const getDefaultQuestionAnswer = useCallback(({ type, answer }: ProcessTalentRegistrationFormDto) => {
    const isMultipleValuesQuestionType =
      type === ProcessTalentRegistrationFormDtoType.SELECT || type === ProcessTalentRegistrationFormDtoType.CHECKBOX;
    // FIXME: delete or uncomment when enum FILE value is clarified what to do with
    // type === ProcessTalentRegistrationFormDtoType.FILE;

    if (answer?.value) {
      return answer?.value;
    }

    return isMultipleValuesQuestionType ? [] : '';
  }, []);

  const computeDefaultValues = useCallback(
    (registrationForm: TalentRegistrationFormAnswersDto, variant: Variant) => {
      const defaultValues: RegistrationFormValues = {};

      registrationForm[variant]?.forEach(question => {
        defaultValues[question.id!] = getDefaultQuestionAnswer(question);
      });

      return defaultValues;
    },
    [getDefaultQuestionAnswer]
  );

  const setDefaultFields = useCallback(
    (data: TalentRegistrationFormAnswersDto) => {
      if (resetDefaultValuesBlocker.current) return;

      const defaultValues = computeDefaultValues(data, infoVariant);
      resetForm(defaultValues);
      resetDefaultValuesBlocker.current = true;
    },
    [computeDefaultValues, infoVariant, resetForm]
  );

  const changeTab = useCallback(
    (variant: Variant) => {
      setInfoVariant(variant);
      resetForm(computeDefaultValues(registrationForm!, variant));
    },
    [computeDefaultValues, registrationForm, resetForm]
  );

  const talentQuestions = useMemo(() => {
    if (!registrationForm || !registrationForm[infoVariant]) return [];

    return registrationForm[infoVariant].filter(({ isActive /*, type  */ }) => {
      if (!isActive) return false;

      // FIXME: delete or uncomment when enum FILE value is clarified what to do with
      // if (type === ProcessTalentRegistrationFormDtoType.FILE && infoVariant === Variant.INTERNAL) return false;

      return true;
    });
  }, [infoVariant, registrationForm]);

  const handleSubmit = useCallback(async () => {
    try {
      const answers = talentQuestions
        .filter(({ id }) => id && getFieldState(id).isDirty)
        .map(({ id }) => ({
          talentQuestionId: id,
          answer: { value: getValues(id!) } as SingleAnswer | ArrayAnswer
        }));
      await updateTalentRegistrationForm({ processId: process.id!, talentId: talentId, data: { answers } });
      resetDefaultValuesBlocker.current = false;
      await queryClient.invalidateQueries(talentRegistrationFormQueryKey);
      if (isBlocked) proceed!();
    } catch {
      showMessage(t('common:error.somethingWentWrong'));
    }
  }, [
    getFieldState,
    getValues,
    isBlocked,
    proceed,
    process.id,
    queryClient,
    showMessage,
    t,
    talentId,
    talentQuestions,
    talentRegistrationFormQueryKey,
    updateTalentRegistrationForm
  ]);

  const handleTabsChange = useCallback(
    async (_e: React.SyntheticEvent, variant: Variant) => {
      if (!formState.isDirty) {
        changeTab(variant);

        return;
      }

      toggleTabChangeConfirmation();
      setProceedToTab(() => () => {
        changeTab(variant);
        setProceedToTab(undefined);
      });
    },
    [changeTab, formState.isDirty, toggleTabChangeConfirmation]
  );

  const handleModalConfirmation = useCallback(async () => {
    try {
      await handleSubmit();
      proceedToTab?.();
    } catch {
      showMessage(t('common:error.somethingWentWrong'));
      setProceedToTab(undefined);
    }
  }, [handleSubmit, proceedToTab, showMessage, t]);

  const handleModalConfirmationFailed = useCallback(async () => {
    showMessage(t('validation:alerts.failed'));
    toggleTabChangeConfirmation();
  }, [showMessage, t, toggleTabChangeConfirmation]);

  const handleModalCancellation = useCallback(async () => {
    toggleTabChangeConfirmation();
  }, [toggleTabChangeConfirmation]);

  const handleFormReset = useCallback(() => {
    resetForm?.();
    proceed?.();
  }, [proceed, resetForm]);

  const { value: { canUpdate: canUpdateTalent } = {} } = useTalentPermissions({
    variables: { talentId: talentId! }
  });

  if (isLoading) {
    return <Loader sx={{ position: 'absolute', top: '50%', left: '50%', translate: '-50% -50%' }} />;
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Typography component="h2" variant="body1Bold" sx={{ padding: 3 }}>
          {t('layouts.talentEditModal.tabs.additionalInfo')}
        </Typography>

        <HorizontalTabs value={infoVariant} onChange={handleTabsChange} sx={{ px: spacing(3) }}>
          <HorizontalTab value={Variant.EXTERNAL} label={t('layouts.talentEditModal.tabs.byTalent')} />
          <HorizontalTab value={Variant.INTERNAL} label={t('layouts.talentEditModal.tabs.byOrganization')} />
        </HorizontalTabs>

        <Box
          sx={{
            flexBasis: 0,
            flexGrow: 1,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: `repeat(1)`,
            gap: spacing(2, 1.5),
            px: spacing(3),
            paddingTop: spacing(3),
            paddingBottom: spacing(3)
          }}
        >
          {talentQuestions.length > 0 ? (
            talentQuestions.map(question => (
              <AdditionalInfoQuestion
                disabled={infoVariant === Variant.EXTERNAL}
                key={question?.id}
                translation={translations && translations[question.id!]}
                talentQuestion={question}
                processId={process.id}
                talentId={talentId}
              />
            ))
          ) : (
            <Box marginY="auto">
              <EmptyState imageUrl="/images/empty-state-illustration.png" imageAlt={t('layouts.talent.talentEmptyAlt')}>
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'center', fontWeight: typography.fontWeightMedium, color: palette.grey[700] }}
                >
                  {t('layouts.talentEditModal.noAdditionalInformation')}
                </Typography>
              </EmptyState>
            </Box>
          )}

          <ConfirmationModal
            open={isTabChangeConfirmationOpen}
            isLoading={isFormRegistrationSaving}
            onConfirm={formMethods.handleSubmit(handleModalConfirmation, handleModalConfirmationFailed)}
            onDiscard={handleModalCancellation}
            title={t('settings:settings.unsavedChangesTitle')}
          />
        </Box>

        {footer ?? <CommonFormFooter sx={{ marginTop: 'auto' }} disableSubmit={!canUpdateTalent} />}
      </Box>

      <ConfirmationModal
        open={isBlocked}
        onConfirm={formMethods.handleSubmit(handleSubmit, handleSubmitFailed)}
        onClose={resetBlocker}
        onDiscard={handleFormReset}
        discardButtonLabel={t('settings:settings.discardButton')}
        title={t('settings:settings.unsavedChangesTitle')}
      />
    </FormProvider>
  );
};

AdditionalInfo.displayName = 'AdditionalInfo';

export default AdditionalInfo;
